import { Redirect, useLocalSearchParams, useRouter } from "expo-router";
import { lazy, Suspense } from "react";
import { StyleSheet, useWindowDimensions } from "react-native";

import { CentralStaffShell } from "../../../components/layout";
import { LoadingState } from "../../../components/ui";
import { resolveCentralLegacyWorkspaceRedirect } from "../../../lib/central-legacy-workspace-routes";
import { staffPath } from "../../../lib/hosting";
import { spacing } from "../../../theme/tokens";
import { resolveAdminLayout } from "../utils/admin-layout";
import { DashboardHome } from "./central-dashboard";
import { WorkspaceNotFound } from "./central-workspace-primitives";

const StaffPermissionsPanel = lazy(async () => {
  const module = await import("../../../components/StaffPermissionsPanel");
  return { default: module.StaffPermissionsPanel };
});
const CentralMyWork = lazy(async () => {
  const module = await import("../../../components/CentralMyWork");
  return { default: module.CentralMyWork };
});
const CentralCaseCrud = lazy(async () => {
  const module = await import("../../../components/CentralCaseCrud");
  return { default: module.CentralCaseCrud };
});
const CentralCallTracking = lazy(async () => {
  const module = await import("../../../components/CentralCallTracking");
  return { default: module.CentralCallTracking };
});
const CentralInquiryQueue = lazy(async () => {
  const module = await import("../../../components/CentralInquiryQueue");
  return { default: module.CentralInquiryQueue };
});
const CentralCaseChecklist = lazy(async () => {
  const module = await import("../../../components/CentralCaseChecklist");
  return { default: module.CentralCaseChecklist };
});
const CentralDocumentLibrary = lazy(async () => {
  const module = await import("../../../components/CentralDocumentLibrary");
  return { default: module.CentralDocumentLibrary };
});
const CentralGeneratedForms = lazy(async () => {
  const module = await import("../../../components/CentralGeneratedForms");
  return { default: module.CentralGeneratedForms };
});
const CentralSignaturePackets = lazy(async () => {
  const module = await import("../../../components/CentralSignaturePackets");
  return { default: module.CentralSignaturePackets };
});
const CentralContractBuilder = lazy(async () => {
  const module = await import("../../../components/CentralContractBuilder");
  return { default: module.CentralContractBuilder };
});
const CentralFinancials = lazy(async () => {
  const module = await import("../../../components/CentralFinancials");
  return { default: module.CentralFinancials };
});
const CentralRegisterCases = lazy(async () => {
  const module = await import("../../../components/CentralRegisterCaseOverview");
  return { default: module.CentralRegisterCases };
});
const CentralCaseOverview = lazy(async () => {
  const module = await import("../../../components/CentralRegisterCaseOverview");
  return { default: module.CentralCaseOverview };
});
const CentralArrangementAssistant = lazy(async () => {
  const module = await import("../../../components/CentralArrangementAssistant");
  return { default: module.CentralArrangementAssistant };
});
const CentralArrangementReview = lazy(async () => {
  const module = await import("../../../components/CentralArrangementReview");
  return { default: module.CentralArrangementReview };
});
const CentralVitalStatisticsReview = lazy(async () => {
  const module = await import("../../../components/CentralVitalStatisticsReview");
  return { default: module.CentralVitalStatisticsReview };
});
const CentralReports = lazy(async () => {
  const module = await import("../../../components/CentralReports");
  return { default: module.CentralReports };
});
const CentralCatalogPriceList = lazy(async () => {
  const module = await import("../../../components/CentralCatalogPriceList");
  return { default: module.CentralCatalogPriceList };
});
const CentralFormTemplateLibrary = lazy(async () => {
  const module = await import("../../../components/CentralFormTemplateLibrary");
  return { default: module.CentralFormTemplateLibrary };
});
const CentralNotificationTemplates = lazy(async () => {
  const module = await import("../../../components/CentralNotificationTemplates");
  return { default: module.CentralNotificationTemplates };
});
const CentralNotificationCenter = lazy(async () => {
  const module = await import("../../../components/CentralNotificationCenter");
  return { default: module.CentralNotificationCenter };
});
const CentralMemorialManagement = lazy(async () => {
  const module = await import("../../../components/CentralMemorialManagement");
  return { default: module.CentralMemorialManagement };
});
const CentralServiceFolder = lazy(async () => {
  const module = await import("../../../components/CentralServiceFolder");
  return { default: module.CentralServiceFolder };
});
const CentralGuestbook = lazy(async () => {
  const module = await import("../../../components/CentralGuestbook");
  return { default: module.CentralGuestbook };
});
const CentralEventWorkspace = lazy(async () => {
  const module = await import("../../../components/CentralEventWorkspace");
  return { default: module.CentralEventWorkspace };
});
const CentralKeepsakeOrders = lazy(async () => {
  const module = await import("../../../components/CentralKeepsakeOrders");
  return { default: module.CentralKeepsakeOrders };
});
const CentralCrmContacts = lazy(async () => {
  const module = await import("../../../components/CentralCrmContacts");
  return { default: module.CentralCrmContacts };
});
const CentralFuneralHomeSettings = lazy(async () => {
  const module = await import("../../../components/CentralFuneralHomeSettings");
  return { default: module.CentralFuneralHomeSettings };
});
const CentralBrandingSettings = lazy(async () => {
  const module = await import("../../../components/CentralBrandingSettings");
  return { default: module.CentralBrandingSettings };
});
const CentralAccountOverview = lazy(async () => {
  const module = await import("../../../components/CentralAccountOverview");
  return { default: module.CentralAccountOverview };
});
const CentralFamilyAccessSupport = lazy(async () => {
  const module = await import(
    "../../../components/CentralFamilyAccessSupport"
  );
  return { default: module.CentralFamilyAccessSupport };
});

const centralScreenAliases: Record<string, string> = {
  assistant: "arrangement-assistant",
  documents: "document-library",
  forms: "generated-forms",
  memorials: "website-memorial-management",
  events: "event-calendar",
  registry: "online-registry",
  "staff-login": "staff-profile",
};

export default function AdminOperationsScreen({
  screenIdOverride,
}: { screenIdOverride?: string } = {}) {
  const { width } = useWindowDimensions();
  const router = useRouter();
  const params = useLocalSearchParams<{
    caseId?: string | string[];
    screenId?: string | string[];
  }>();
  const routeScreenId = Array.isArray(params.screenId)
    ? params.screenId[0]
    : params.screenId;
  const requestedCaseId = Array.isArray(params.caseId)
    ? params.caseId[0] ?? ""
    : params.caseId ?? "";
  const requestedScreenId = screenIdOverride ?? routeScreenId;
  const screenId = requestedScreenId
    ? (centralScreenAliases[requestedScreenId] ?? requestedScreenId)
    : requestedScreenId;
  const legacyRedirect =
    resolveCentralLegacyWorkspaceRedirect(requestedScreenId, requestedCaseId) ??
    resolveCentralLegacyWorkspaceRedirect(screenId, requestedCaseId);
  const { showDesktopShell: isDesktop, useWideContentColumns } =
    resolveAdminLayout(width);
  const isWorkspace = Boolean(screenId && screenId !== "staff-dashboard");

  if (!screenId || screenId === "staff-dashboard") {
    return <Redirect href="/events" />;
  }

  if (legacyRedirect) {
    return <Redirect href={staffPath(legacyRedirect) as never} />;
  }

  return (
    <CentralStaffShell contentContainerStyle={styles.operationsContent} showCaseIdentity={screenId !== "case-overview"}>
      <Suspense
        fallback={
          <LoadingState
            label="Loading workspace"
          />
        }
      >
        {screenId === "my-work" ? (
          <CentralMyWork />
        ) : screenId === "case-list" ? (
          <CentralRegisterCases />
        ) : screenId === "create-case" ? (
          <CentralCaseCrud
            mode="create"
            onCreated={(caseRecord) => router.replace(`/workspace/case-overview?caseId=${encodeURIComponent(caseRecord.id)}` as never)}
          />
        ) : screenId === "call-tracking" ? (
          <CentralCallTracking />
        ) : screenId === "inquiries" ? (
          <CentralInquiryQueue />
        ) : screenId === "case-checklist" ? (
          <CentralCaseChecklist />
        ) : screenId === "document-library" ? (
          <CentralDocumentLibrary />
        ) : screenId === "generated-forms" ? (
          <CentralGeneratedForms />
        ) : screenId === "signature-packet" ? (
          <CentralSignaturePackets />
        ) : screenId === "contract-builder" ? (
          <CentralContractBuilder />
        ) : screenId === "financials" ? (
          <CentralFinancials />
        ) : screenId === "case-overview" ? (
          <CentralCaseOverview />
        ) : screenId === "arrangement-assistant" ? (
          <CentralArrangementAssistant />
        ) : screenId === "staff-arrangement-review" ? (
          <CentralArrangementReview />
        ) : screenId === "vital-statistics-review" ? (
          <CentralVitalStatisticsReview />
        ) : screenId === "reports-exports" ? (
          <CentralReports />
        ) : screenId === "catalog-price-list-management" ? (
          <CentralCatalogPriceList />
        ) : screenId === "form-template-library" || screenId === "form-template-editor" ? (
          <CentralFormTemplateLibrary editorMode={screenId === "form-template-editor"} />
        ) : screenId === "notification-templates" ? (
          <CentralNotificationTemplates />
        ) : screenId === "notifications" ? (
          <CentralNotificationCenter />
        ) : screenId === "website-memorial-management" ? (
          <CentralMemorialManagement />
        ) : screenId === "digital-service-folder-builder" ? (
          <CentralServiceFolder />
        ) : screenId === "guestbook-moderation" ? (
          <CentralGuestbook />
        ) : screenId === "guest-contacts" ? (
          <CentralCrmContacts />
        ) : screenId === "keepsake-orders" ? (
          <CentralKeepsakeOrders />
        ) : ["event-calendar", "online-registry"].includes(screenId ?? "") ? (
          <CentralEventWorkspace />
        ) : screenId === "funeral-home-settings" ? (
          <CentralFuneralHomeSettings />
        ) : screenId === "branding-logo-management" ? (
          <CentralBrandingSettings />
        ) : screenId === "staff-profile" ? (
          <CentralAccountOverview />
        ) : screenId === "family-access-support" ? (
          <CentralFamilyAccessSupport />
        ) : screenId === "staff-permissions" ? (
          <StaffPermissionsPanel />
        ) : isWorkspace ? (
          <WorkspaceNotFound screenId={screenId ?? ""} />
        ) : (
          <DashboardHome
            isDesktop={isDesktop}
            useWideContentColumns={useWideContentColumns}
          />
        )}
      </Suspense>
    </CentralStaffShell>
  );
}

const styles = StyleSheet.create({
  operationsContent: {
    gap: spacing.md,
  },
});
